import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  TrendingUp,
  TrendingDown,
  Notifications,
  Settings,
  FilterList,
  Download,
  Refresh,
  Search,
  Add,
  Edit,
  MoreVert,
  Timeline,
  Analytics,
  Speed,
  AccessTime,
  Assignment,
  People,
  Support,
  Phone,
  Email,
  Sms,
  NotificationsActive,
  PlayArrow,
  Pause,
  Stop,
  ExpandMore,
  InfoOutlined,
  ReportProblem,
  PriorityHigh,
  Escalator,
  AutorenewOutlined,
  AlarmOn,
  AlarmOff,
  HealthAndSafety,
  MonitorHeart,
  DeviceHub,
  NetworkCheck,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ComposedChart } from 'recharts';

interface SLAMetric {
  id: string;
  title: string;
  currentValue: number;
  targetValue: number;
  unit: string;
  status: 'healthy' | 'warning' | 'critical';
  trend: number;
  trendDirection: 'up' | 'down';
  description: string;
  icon: React.ReactNode;
}

interface SLAAlert {
  id: string;
  service: string;
  type: 'response-delay' | 'breach' | 'at-risk' | 'escalation';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  target: string;
  current: string;
  timeRemaining: string;
  responsibleTeam: string;
  responsiblePerson: string;
  createdAt: string;
  updatedAt: string;
  escalated: boolean;
  acknowledged: boolean;
  actions: string[];
}

interface ServiceSLA {
  id: string;
  name: string;
  category: 'booking' | 'support' | 'verification' | 'dispute' | 'maintenance';
  status: 'operational' | 'degraded' | 'major-outage' | 'maintenance';
  uptime: number;
  responseTime: number;
  targetResponseTime: number;
  availability: number;
  targetAvailability: number;
  breaches: number;
  lastIncident: string;
  nextMaintenance: string;
  icon: React.ReactNode;
}

interface SLAConfiguration {
  id: string;
  service: string;
  metric: string;
  target: number;
  warningThreshold: number;
  criticalThreshold: number;
  unit: string;
  enabled: boolean;
  escalationRules: EscalationRule[];
}

interface EscalationRule {
  id: string;
  level: number;
  triggerAfter: string;
  assignTo: string;
  notificationChannel: 'email' | 'sms' | 'phone' | 'slack';
  enabled: boolean;
}

interface PerformanceTrend {
  timestamp: string;
  responseTime: number;
  availability: number;
  throughput: number;
  errorRate: number;
}

interface BreachReport {
  id: string;
  service: string;
  metric: string;
  target: number;
  actual: number;
  duration: string;
  impact: 'low' | 'medium' | 'high' | 'critical';
  rootCause: string;
  resolution: string;
  occurred: string;
  resolved: string;
  affectedUsers: number;
}

const SLAMonitoringPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState('24h');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [escalationDialogOpen, setEscalationDialogOpen] = useState(false);
  const [alertMenuAnchor, setAlertMenuAnchor] = useState<null | HTMLElement>(null);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState<SLAAlert | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [slaConfig, setSlaConfig] = useState({
    responseTimeTarget: 4.0,
    responseTimeWarning: 3.0,
    responseTimeCritical: 4.5,
    availabilityTarget: 99.5,
    availabilityWarning: 99.0,
    availabilityCritical: 98.0,
    resolutionTimeTarget: 24.0,
    resolutionTimeWarning: 20.0,
    resolutionTimeCritical: 26.0,
    emailNotifications: true,
    smsNotifications: false,
    slackNotifications: true,
    escalationLevel1: 30,
    escalationLevel2: 60,
    escalationLevel3: 120
  });

  // Sample data - in real implementation, this would come from APIs
  const slaMetrics: SLAMetric[] = [
    {
      id: 'responseTime',
      title: 'Avg Response Time',
      currentValue: 2.3,
      targetValue: 4.0,
      unit: 'hours',
      status: 'healthy',
      trend: -12.5,
      trendDirection: 'down',
      description: 'Average time to first response',
      icon: <AccessTime />
    },
    {
      id: 'availability',
      title: 'System Availability',
      currentValue: 99.8,
      targetValue: 99.5,
      unit: '%',
      status: 'healthy',
      trend: 0.2,
      trendDirection: 'up',
      description: 'Overall system uptime',
      icon: <MonitorHeart />
    },
    {
      id: 'resolution',
      title: 'Resolution Time',
      currentValue: 18.5,
      targetValue: 24.0,
      unit: 'hours',
      status: 'warning',
      trend: 8.3,
      trendDirection: 'up',
      description: 'Average time to resolve issues',
      icon: <Assignment />
    },
    {
      id: 'breaches',
      title: 'SLA Breaches',
      currentValue: 3,
      targetValue: 0,
      unit: 'count',
      status: 'critical',
      trend: 50,
      trendDirection: 'up',
      description: 'Number of SLA violations',
      icon: <ReportProblem />
    }
  ];

  const activeAlerts: SLAAlert[] = [
    {
      id: '1',
      service: 'Booking Support',
      type: 'response-delay',
      severity: 'critical',
      title: 'Response Time SLA Breach',
      description: 'Support ticket #BK-2024-001 has exceeded 4-hour response SLA',
      target: '4 hours',
      current: '6.2 hours',
      timeRemaining: 'Breached 2h 12m ago',
      responsibleTeam: 'Customer Support',
      responsiblePerson: 'Sarah Johnson',
      createdAt: '2024-01-15T08:00:00Z',
      updatedAt: '2024-01-15T14:12:00Z',
      escalated: true,
      acknowledged: false,
      actions: ['Acknowledge', 'Escalate', 'Reassign']
    },
    {
      id: '2',
      service: 'Driver Verification',
      type: 'at-risk',
      severity: 'high',
      title: 'Verification Processing Delay',
      description: 'Driver verification #DV-2024-089 approaching 24-hour SLA limit',
      target: '24 hours',
      current: '18.5 hours',
      timeRemaining: '5.5 hours remaining',
      responsibleTeam: 'Verification Team',
      responsiblePerson: 'Mike Chen',
      createdAt: '2024-01-15T12:30:00Z',
      updatedAt: '2024-01-15T14:00:00Z',
      escalated: false,
      acknowledged: true,
      actions: ['Expedite', 'Assign Priority', 'Request Update']
    },
    {
      id: '3',
      service: 'Dispute Resolution',
      type: 'escalation',
      severity: 'medium',
      title: 'Escalation Required',
      description: 'Dispute #DP-2024-156 requires supervisor review after 48 hours',
      target: '48 hours',
      current: '49.2 hours',
      timeRemaining: 'Breached 1h 12m ago',
      responsibleTeam: 'Dispute Team',
      responsiblePerson: 'Alex Rivera',
      createdAt: '2024-01-13T10:15:00Z',
      updatedAt: '2024-01-15T13:27:00Z',
      escalated: true,
      acknowledged: true,
      actions: ['Supervisor Review', 'Close', 'Extend SLA']
    }
  ];

  const serviceSLAs: ServiceSLA[] = [
    {
      id: 'booking',
      name: 'Booking Service',
      category: 'booking',
      status: 'operational',
      uptime: 99.8,
      responseTime: 1.2,
      targetResponseTime: 2.0,
      availability: 99.95,
      targetAvailability: 99.5,
      breaches: 0,
      lastIncident: '2024-01-10T15:30:00Z',
      nextMaintenance: '2024-01-20T02:00:00Z',
      icon: <Assignment />
    },
    {
      id: 'support',
      name: 'Customer Support',
      category: 'support',
      status: 'degraded',
      uptime: 98.5,
      responseTime: 4.8,
      targetResponseTime: 4.0,
      availability: 99.2,
      targetAvailability: 99.0,
      breaches: 2,
      lastIncident: '2024-01-15T08:00:00Z',
      nextMaintenance: '2024-01-18T01:00:00Z',
      icon: <Support />
    },
    {
      id: 'verification',
      name: 'Verification Service',
      category: 'verification',
      status: 'operational',
      uptime: 99.6,
      responseTime: 18.5,
      targetResponseTime: 24.0,
      availability: 99.8,
      targetAvailability: 99.5,
      breaches: 1,
      lastIncident: '2024-01-12T11:20:00Z',
      nextMaintenance: '2024-01-25T03:00:00Z',
      icon: <HealthAndSafety />
    },
    {
      id: 'disputes',
      name: 'Dispute Resolution',
      category: 'dispute',
      status: 'operational',
      uptime: 99.9,
      responseTime: 35.2,
      targetResponseTime: 48.0,
      availability: 99.9,
      targetAvailability: 99.0,
      breaches: 1,
      lastIncident: '2024-01-08T09:45:00Z',
      nextMaintenance: '2024-01-22T02:30:00Z',
      icon: <People />
    }
  ];

  const performanceTrends: PerformanceTrend[] = [
    { timestamp: '00:00', responseTime: 2.1, availability: 99.8, throughput: 1250, errorRate: 0.1 },
    { timestamp: '04:00', responseTime: 1.8, availability: 99.9, throughput: 980, errorRate: 0.05 },
    { timestamp: '08:00', responseTime: 3.2, availability: 99.6, throughput: 1890, errorRate: 0.2 },
    { timestamp: '12:00', responseTime: 4.1, availability: 99.5, throughput: 2340, errorRate: 0.3 },
    { timestamp: '16:00', responseTime: 3.8, availability: 99.7, throughput: 2100, errorRate: 0.15 },
    { timestamp: '20:00', responseTime: 2.9, availability: 99.8, throughput: 1650, errorRate: 0.1 },
  ];

  const breachReports: BreachReport[] = [
    {
      id: '1',
      service: 'Customer Support',
      metric: 'Response Time',
      target: 4.0,
      actual: 6.2,
      duration: '2h 12m',
      impact: 'high',
      rootCause: 'Staff shortage during peak hours',
      resolution: 'Additional staff assigned, escalation process activated',
      occurred: '2024-01-15T08:00:00Z',
      resolved: '2024-01-15T14:12:00Z',
      affectedUsers: 23
    },
    {
      id: '2',
      service: 'Verification Service',
      metric: 'Processing Time',
      target: 24.0,
      actual: 28.3,
      duration: '4h 18m',
      impact: 'medium',
      rootCause: 'System maintenance overlap',
      resolution: 'Maintenance rescheduled, additional verification staff deployed',
      occurred: '2024-01-14T16:30:00Z',
      resolved: '2024-01-14T20:48:00Z',
      affectedUsers: 7
    }
  ];

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'healthy':
      case 'operational': return '#4CAF50';
      case 'warning':
      case 'degraded': return '#FF9800';
      case 'critical':
      case 'major-outage': return '#F44336';
      case 'maintenance': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#F44336';
      case 'high': return '#FF5722';
      case 'medium': return '#FF9800';
      case 'low': return '#FFC107';
      default: return '#9E9E9E';
    }
  };

  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'critical': return '#F44336';
      case 'high': return '#FF5722';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  const formatDuration = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m ago`;
  };

  const filteredAlerts = activeAlerts.filter(alert => {
    const matchesSearch = alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.service.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || alert.severity === severityFilter;
    const matchesService = serviceFilter === 'all' || alert.service.toLowerCase().includes(serviceFilter.toLowerCase());
    return matchesSearch && matchesSeverity && matchesService;
  });

  const handleAlertAction = (alertId: string, action: string) => {
    setAlertMenuAnchor(null);
  };

  // Refresh Handler
  const handleRefresh = async () => {
    setRefreshing(true);

    // Simulate API call to refresh SLA data
    await new Promise(resolve => setTimeout(resolve, 1500));

    // In real implementation, this would fetch fresh data from the API

    setLastRefreshTime(new Date());
    setRefreshing(false);
    alert('SLA monitoring data refreshed successfully!');
  };

  // Time Range Change Handler
  const handleTimeRangeChange = (newTimeRange: string) => {
    setTimeRange(newTimeRange);

    // In real implementation, this would trigger data fetching with new time range

    // Show feedback to user
    const rangeText = {
      '1h': 'last hour',
      '24h': 'last 24 hours',
      '7d': 'last 7 days',
      '30d': 'last 30 days'
    }[newTimeRange] || newTimeRange;

    // You could show a toast notification here instead of alert
    // For now, we'll log it
  };

  // Configuration Save Handler
  const handleSaveConfiguration = () => {
    // In real implementation, this would call an API to save the configuration

    setConfigDialogOpen(false);
    alert('SLA configuration saved successfully! New thresholds and notification rules are now active.');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            SLA Monitoring
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time service level agreement monitoring and alerting system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={realTimeEnabled} onChange={(e) => setRealTimeEnabled(e.target.checked)} />}
            label="Real-time"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} onChange={(e) => handleTimeRangeChange(e.target.value)}>
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24h</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outlined" startIcon={<Settings />} onClick={() => setConfigDialogOpen(true)}>
            Configure
          </Button>
        </Box>
      </Box>

      {/* Key SLA Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {slaMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.id}>
            <Card sx={{ 
              borderLeft: `4px solid ${getStatusColor(metric.status)}`,
              height: '100%'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    backgroundColor: getStatusColor(metric.status) + '20',
                    color: getStatusColor(metric.status),
                    mr: 2
                  }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {metric.title}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                  <Typography variant="h4" component="div" sx={{ mr: 1 }}>
                    {metric.currentValue}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {metric.unit}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Target: {metric.targetValue} {metric.unit}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {metric.trendDirection === 'up' ? (
                      <TrendingUp sx={{ color: metric.status === 'critical' ? '#F44336' : '#4CAF50', fontSize: 16 }} />
                    ) : (
                      <TrendingDown sx={{ color: metric.status === 'healthy' ? '#4CAF50' : '#F44336', fontSize: 16 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: metric.trendDirection === 'up' 
                          ? (metric.status === 'critical' ? '#F44336' : '#4CAF50') 
                          : (metric.status === 'healthy' ? '#4CAF50' : '#F44336'),
                        ml: 0.5
                      }}
                    >
                      {metric.trend > 0 ? '+' : ''}{metric.trend}%
                    </Typography>
                  </Box>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={Math.min((metric.currentValue / metric.targetValue) * 100, 100)}
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: getStatusColor(metric.status) + '20',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getStatusColor(metric.status)
                    }
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {metric.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Critical Alerts Banner */}
      {activeAlerts.filter(alert => alert.severity === 'critical').length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PriorityHigh />
              Critical SLA Violations Detected
            </Box>
          </AlertTitle>
          {activeAlerts.filter(alert => alert.severity === 'critical').length} critical SLA breaches require immediate attention.
          <Button size="small" variant="outlined" sx={{ ml: 2 }}>
            View All Critical
          </Button>
        </Alert>
      )}

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Dashboard />} label="Overview" />
          <Tab icon={<Warning />} label={`Active Alerts (${activeAlerts.length})`} />
          <Tab icon={<DeviceHub />} label="Service Status" />
          <Tab icon={<Timeline />} label="Performance Trends" />
          <Tab icon={<ReportProblem />} label="Breach Reports" />
          <Tab icon={<Settings />} label="SLA Configuration" />
          <Tab icon={<Escalator />} label="Escalation Management" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Real-time Performance Chart */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Real-time Performance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {realTimeEnabled && (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                        <CircularProgress size={12} sx={{ mr: 1 }} />
                        <Typography variant="caption">Live</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={performanceTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timestamp" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="responseTime" stroke="#FF9800" strokeWidth={2} name="Response Time (hrs)" />
                    <Line type="monotone" dataKey="availability" stroke="#4CAF50" strokeWidth={2} name="Availability %" />
                    <Bar dataKey="errorRate" fill="#F44336" name="Error Rate %" opacity={0.3} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Service Health Summary */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Health Summary
                </Typography>
                <List>
                  {serviceSLAs.map((service) => (
                    <ListItem key={service.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Box sx={{ color: getStatusColor(service.status) }}>
                          {service.icon}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={service.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {service.uptime}% uptime • {service.responseTime}h avg response
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={service.uptime}
                              sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={service.status}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(service.status) + '20',
                            color: getStatusColor(service.status),
                            textTransform: 'capitalize'
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Breach Reports */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent SLA Breaches
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell>Metric</TableCell>
                        <TableCell>Target vs Actual</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Impact</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breachReports.slice(0, 3).map((breach) => (
                        <TableRow key={breach.id}>
                          <TableCell>{breach.service}</TableCell>
                          <TableCell>{breach.metric}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                Target: {breach.target}h
                              </Typography>
                              <Typography variant="body2" color="error.main">
                                Actual: {breach.actual}h
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{breach.duration}</TableCell>
                          <TableCell>
                            <Chip
                              label={breach.impact}
                              size="small"
                              sx={{
                                backgroundColor: getImpactColor(breach.impact) + '20',
                                color: getImpactColor(breach.impact),
                                textTransform: 'capitalize'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Resolved"
                              size="small"
                              color="success"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Active SLA Alerts ({filteredAlerts.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" startIcon={<NotificationsActive />}>
                  Bulk Actions
                </Button>
                <Button variant="outlined" startIcon={<Download />}>
                  Export
                </Button>
              </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Severity</InputLabel>
                <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                  <MenuItem value="all">All Severity</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Service</InputLabel>
                <Select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
                  <MenuItem value="all">All Services</MenuItem>
                  <MenuItem value="booking">Booking</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="verification">Verification</MenuItem>
                  <MenuItem value="dispute">Disputes</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Alerts List */}
            <Grid container spacing={3}>
              {filteredAlerts.map((alert) => (
                <Grid item xs={12} key={alert.id}>
                  <Card variant="outlined" sx={{
                    borderLeft: `4px solid ${getSeverityColor(alert.severity)}`,
                    backgroundColor: alert.acknowledged ? 'action.hover' : 'background.paper'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: getSeverityColor(alert.severity) + '20',
                            color: getSeverityColor(alert.severity)
                          }}>
                            {alert.type === 'breach' ? <Error /> : 
                             alert.type === 'at-risk' ? <Warning /> :
                             alert.type === 'escalation' ? <Escalator /> : <Schedule />}
                          </Box>
                          <Box>
                            <Typography variant="h6" component="div">
                              {alert.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {alert.service} • {alert.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={alert.severity}
                            size="small"
                            sx={{
                              backgroundColor: getSeverityColor(alert.severity) + '20',
                              color: getSeverityColor(alert.severity),
                              textTransform: 'capitalize'
                            }}
                          />
                          {alert.escalated && (
                            <Chip
                              label="Escalated"
                              size="small"
                              color="warning"
                              icon={<Escalator />}
                            />
                          )}
                          {alert.acknowledged && (
                            <Chip
                              label="Acknowledged"
                              size="small"
                              color="success"
                              icon={<CheckCircle />}
                            />
                          )}
                        </Box>
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">Target</Typography>
                          <Typography variant="body2" fontWeight="medium">{alert.target}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">Current</Typography>
                          <Typography variant="body2" fontWeight="medium" color="error.main">
                            {alert.current}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">Time Status</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {alert.timeRemaining}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" color="text.secondary">Responsible</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {alert.responsiblePerson}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Created: {formatDuration(alert.createdAt)} • Updated: {formatDuration(alert.updatedAt)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {alert.actions.map((action) => (
                            <Button
                              key={action}
                              size="small"
                              variant="outlined"
                              onClick={() => handleAlertAction(alert.id, action)}
                            >
                              {action}
                            </Button>
                          ))}
                          <IconButton size="small" onClick={(e) => setAlertMenuAnchor(e.currentTarget)}>
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Service Status Dashboard
            </Typography>
            <Grid container spacing={3}>
              {serviceSLAs.map((service) => (
                <Grid item xs={12} md={6} lg={4} key={service.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          p: 1, 
                          borderRadius: 1, 
                          backgroundColor: getStatusColor(service.status) + '20',
                          color: getStatusColor(service.status),
                          mr: 2
                        }}>
                          {service.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div">
                            {service.name}
                          </Typography>
                          <Chip
                            label={service.status}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(service.status) + '20',
                              color: getStatusColor(service.status),
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Uptime</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {service.uptime}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={service.uptime}
                          sx={{ height: 6, borderRadius: 3, mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Target: {service.targetAvailability}%
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Response Time</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {service.responseTime}h / {service.targetResponseTime}h
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">SLA Breaches</Typography>
                        <Typography variant="body2" fontWeight="medium" color={service.breaches > 0 ? 'error.main' : 'text.primary'}>
                          {service.breaches}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Last Incident</Typography>
                        <Typography variant="body2">
                          {formatDuration(service.lastIncident)}
                        </Typography>
                      </Box>

                      <Button variant="outlined" fullWidth size="small">
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Additional tabs would continue here... */}
      
      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Settings />
            <Box>
              <Typography variant="h6">
                SLA Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure SLA targets, thresholds, and notification rules
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Response Time Thresholds */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Response Time Thresholds
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Target (hours)"
                value={slaConfig.responseTimeTarget}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  responseTimeTarget: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Target response time"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Warning (hours)"
                value={slaConfig.responseTimeWarning}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  responseTimeWarning: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Warning threshold"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Critical (hours)"
                value={slaConfig.responseTimeCritical}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  responseTimeCritical: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Critical threshold"
              />
            </Grid>

            {/* Availability Thresholds */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                System Availability Thresholds
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Target (%)"
                value={slaConfig.availabilityTarget}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  availabilityTarget: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Target availability"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Warning (%)"
                value={slaConfig.availabilityWarning}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  availabilityWarning: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Warning threshold"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Critical (%)"
                value={slaConfig.availabilityCritical}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  availabilityCritical: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Critical threshold"
              />
            </Grid>

            {/* Resolution Time Thresholds */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Resolution Time Thresholds
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Target (hours)"
                value={slaConfig.resolutionTimeTarget}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  resolutionTimeTarget: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Target resolution time"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Warning (hours)"
                value={slaConfig.resolutionTimeWarning}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  resolutionTimeWarning: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Warning threshold"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Critical (hours)"
                value={slaConfig.resolutionTimeCritical}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  resolutionTimeCritical: parseFloat(e.target.value)
                })}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Critical threshold"
              />
            </Grid>

            {/* Notification Settings */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Notification Settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={slaConfig.emailNotifications}
                    onChange={(e) => setSlaConfig({
                      ...slaConfig,
                      emailNotifications: e.target.checked
                    })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" />
                    Email Notifications
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={slaConfig.smsNotifications}
                    onChange={(e) => setSlaConfig({
                      ...slaConfig,
                      smsNotifications: e.target.checked
                    })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Sms fontSize="small" />
                    SMS Notifications
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={slaConfig.slackNotifications}
                    onChange={(e) => setSlaConfig({
                      ...slaConfig,
                      slackNotifications: e.target.checked
                    })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsActive fontSize="small" />
                    Slack Notifications
                  </Box>
                }
              />
            </Grid>

            {/* Escalation Rules */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Escalation Rules (minutes)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Level 1 Escalation"
                value={slaConfig.escalationLevel1}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  escalationLevel1: parseInt(e.target.value)
                })}
                inputProps={{ min: 0, step: 5 }}
                helperText="Escalate after (min)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Level 2 Escalation"
                value={slaConfig.escalationLevel2}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  escalationLevel2: parseInt(e.target.value)
                })}
                inputProps={{ min: 0, step: 5 }}
                helperText="Escalate after (min)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Level 3 Escalation"
                value={slaConfig.escalationLevel3}
                onChange={(e) => setSlaConfig({
                  ...slaConfig,
                  escalationLevel3: parseInt(e.target.value)
                })}
                inputProps={{ min: 0, step: 5 }}
                helperText="Escalate after (min)"
              />
            </Grid>

            {/* Info Alert */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Changes to SLA configuration will apply immediately and affect all monitoring services. Make sure to review and test notification settings.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveConfiguration} startIcon={<Settings />}>
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Actions Menu */}
      <Menu
        anchorEl={alertMenuAnchor}
        open={Boolean(alertMenuAnchor)}
        onClose={() => setAlertMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleAlertAction('', 'view-details')}>
          <ListItemIcon><InfoOutlined /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAlertAction('', 'snooze')}>
          <ListItemIcon><Schedule /></ListItemIcon>
          <ListItemText>Snooze Alert</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAlertAction('', 'assign')}>
          <ListItemIcon><Assignment /></ListItemIcon>
          <ListItemText>Assign to Team</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAlertAction('', 'close')}>
          <ListItemIcon><CheckCircle /></ListItemIcon>
          <ListItemText>Mark Resolved</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SLAMonitoringPage;